import type { UpdateLineItemApprovedVariables } from '../useMutationUpdateLineItemApproved/types';
import type {
  ApproveInvoiceMutationOptions,
  ApproveInvoiceVariables,
} from './types';

import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import type { UUID } from '~/types/common';

import { queryKeysInvoice } from '../queryKeys';
import { updateInvoiceDataApproved } from '../updateInvoiceDataApproved/updateInvoiceDataApproved';
import { MUTATION_KEY_UPDATE_LINE_ITEM_APPROVED } from '../useMutationUpdateLineItemApproved/useMutationUpdateLineItemApproved';
import { fetchInvoiceDetail } from '../useQueryInvoiceDetail/fetchInvoiceDetail';

import { approveInvoice } from './approveInvoice';

/** Identifies approvals in flight, so the line table can lock for their duration. */
export const MUTATION_KEY_APPROVE_INVOICE = ['invoice', 'approve'] as const;

/** Waits for this invoice's line saves in flight; rejects if one fails. */
async function waitForLineSaves(queryClient: QueryClient, invoiceId: UUID) {
  const mutationCache = queryClient.getMutationCache();
  const saves = mutationCache.findAll({
    mutationKey: MUTATION_KEY_UPDATE_LINE_ITEM_APPROVED,
    predicate: (mutation) =>
      (mutation.state.variables as UpdateLineItemApprovedVariables | undefined)
        ?.invoiceId === invoiceId,
    status: 'pending',
  });
  const isSaving = () => saves.some((save) => save.state.status === 'pending');

  await new Promise<void>((resolve) => {
    if (!isSaving()) {
      resolve();
      return;
    }

    const unsubscribe = mutationCache.subscribe(() => {
      if (!isSaving()) {
        unsubscribe();
        resolve();
      }
    });
  });

  const failedSave = saves.find((save) => save.state.status === 'error');

  if (failedSave?.state.error) {
    throw failedSave.state.error;
  }
}

// The reload must see every line save, or the saved totals miss an edit.
async function approveInvoiceWithDataApproved(
  { getDataApproved, invoiceId }: ApproveInvoiceVariables,
  queryClient: QueryClient,
) {
  await waitForLineSaves(queryClient, invoiceId);

  const payload = getDataApproved(await fetchInvoiceDetail(invoiceId));

  if (payload) {
    await updateInvoiceDataApproved({ invoiceId, payload });
  }

  return approveInvoice(invoiceId);
}

/**
 * Mutation hook for approving an invoice.
 *
 * Waits for this invoice's line saves in flight, reloads the invoice, saves
 * the approved totals the caller derives from it, then approves; a failed save
 * of either kind skips the approval. Invalidates detail, timeline, and V2 list
 * queries before settlement. A refused approval still records a new check run.
 *
 * @param options - Optional TanStack Query mutation options.
 * @returns A mutation object to trigger the approval.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/approve_invoice_invoices__invoice_id__approve_post
 */
export function useMutationApproveInvoice(
  options?: ApproveInvoiceMutationOptions,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (variables) =>
      approveInvoiceWithDataApproved(variables, queryClient),
    mutationKey: MUTATION_KEY_APPROVE_INVOICE,
    async onSettled(data, error, variables, onMutateResult, context) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeysInvoice.getDetail(variables.invoiceId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysInvoice.getTimeline(variables.invoiceId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysInvoice.getAllV2({}),
        }),
      ]);

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
  });
}
